import { createFileRoute, Link } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    useDisclosure,
} from "@heroui/react";
import TextInput from "@/components/inputs/TextInput";
import corn from "@/assets/images/logo-corn@2x.png";
import ntimeLogo from "@/assets/images/H-NTime-R.png";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";

export const Route = createFileRoute("/(auth)/auth")({
    component: RouteComponent,
});

const schema = object().shape({
    username: string().email().required().label("Email"),
    password: string().min(8).required().label("Password"),
});

const firstTimeForm = object().shape({
    learnMoreEmail: string().email().required().label("Email"),
});

type FormType = InferType<typeof schema>;

function RouteComponent() {
    const { axiosRequest } = useRequest();
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const { signIn, signInMutate } = useAuth();
    const f = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    const handleSubmit = (data: FormType) => {
        signIn(data);
    };

    const firstTimeF = useForm({
        resolver: yupResolver(firstTimeForm),
        mode: "onChange",
    });

    const handleFirst = async ({
        learnMoreEmail: email,
    }: {
        learnMoreEmail: string;
    }) => {
        axiosRequest({
            url: "learn-more/",
            method: "post",
            data: {
                email,
            },
        }).then(() => {
            onOpen();
        });
    };

    const blurb = (
        <div className="p-4 flex flex-col gap-4 pt-12 w-full">
            <div className="text-green-400 font-bold text-lg">
                N-Time® Insights
            </div>
            <div className="text-3xl">
                Profitable and confident Nitrogen management
            </div>
            <div>
                N-Time optimizes your nitrogen applications to achieve the most
                profitable outcome on your farm. It does this by measuring crop
                N demand and suggesting when, where, and how much N fertilizer
                to apply. N-Time has been used to achieve NCGA Low N contest
                championship wins and has delivered $24/ac improvement in
                profitability in benchmarking trials. Are you ready to use
                N-Time to revolutionize your Nitrogen management?
            </div>
        </div>
    );

    const [isVisible, setIsVisible] = useState(false);

    return (
        <div className="flex flex-col items-center md:grid md:grid-cols-[1fr_2fr] h-screen w-screen justify-items-center md:p-0">
            <div className="bg-brandTeal-500 w-full h-full hidden md:block">
                <div className="relative h-full w-full text-white">
                    {blurb}
                    <img
                        src={corn}
                        className="absolute translate-x-[51.25%] w-[20rem] bottom-0 right-0"
                    />
                </div>
            </div>
            <div className="pt-8 px-2 w-full ">
                <FormProvider {...f}>
                    <form onSubmit={f.handleSubmit(handleSubmit)}>
                        <div className="flex flex-col  w-[90%] mx-auto  items-stretch  md:max-w-[25rem] gap-2">
                            <img src={ntimeLogo} className="px-8" />
                            <TextInput name="username" label="Email" />
                            <TextInput
                                name="password"
                                label="Password"
                                type={isVisible ? "text" : "password"}
                                endContent={
                                    <Button
                                        isIconOnly
                                        variant="flat"
                                        size="sm"
                                        onPress={() => setIsVisible(!isVisible)}
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                isVisible ? faEyeSlash : faEye
                                            }
                                        />
                                    </Button>
                                }
                            ></TextInput>
                            {signInMutate.isError && (
                                <div className="text-sm text-red-500">
                                    Username or password is incorrect
                                </div>
                            )}
                            <Link
                                to="/password/reset"
                                className="text-brandGreen-500 text-sm my-2"
                            >
                                Forgot Password?
                            </Link>
                            <Button
                                type="submit"
                                color="primary"
                                variant="solid"
                                isLoading={signInMutate.isPending}
                            >
                                Sign In
                            </Button>
                        </div>
                    </form>
                </FormProvider>
                <div className="mt-8">
                    <FormProvider {...firstTimeF}>
                        <form onSubmit={firstTimeF.handleSubmit(handleFirst)}>
                            <div className="flex flex-col w-[90%] mx-auto md:max-w-[25rem] gap-4">
                                First time here?
                                <TextInput
                                    name="learnMoreEmail"
                                    label="Email"
                                />
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="solid"
                                    fullWidth
                                >
                                    Learn More
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
            <div className="md:hidden bg-brandNavy-500 mt-12 text-white flex-1 flex flex-col ">
                {blurb}
            </div>
            {isOpen && (
                <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
                    <ModalContent>
                        <ModalBody>
                            Thank you for your interest in N-Time! Please check
                            your email for a message about next steps to get
                            started.
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
        </div>
    );
}
